import React, { useState, useMemo } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULT_FROM_DATE = '2026-01-01';
const todayStr = () => new Date().toISOString().split('T')[0];

const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-PK') : '—';
const fmtMoney = n => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatArea = (kanals, marlas) => {
  const k = parseInt(kanals || 0);
  const m = parseInt(marlas || 0);
  if (k === 0 && m === 0) return '';
  if (m === 0) return `${k} kanal`;
  if (k === 0) return `${m} marla`;
  return `${k} kanal ${m} marla`;
};

const CropFinanceReportModal = ({ type, crops, onClose }) => {
  const isExpense = type === 'expense';
  const accent = isExpense ? '#e74c3c' : '#27ae60';
  const title = isExpense ? 'Crop-wise Expense Report' : 'Crop-wise Income Report';

  const [cropName, setCropName] = useState('');
  const [landId, setLandId] = useState('all');
  const [fromDate, setFromDate] = useState(DEFAULT_FROM_DATE);
  const [toDate, setToDate] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  // Distinct crop names from the crops list
  const cropNameOptions = useMemo(() => {
    const map = new Map();
    (crops || []).forEach(c => {
      if (!c.crop_name) return;
      if (!map.has(c.crop_name)) map.set(c.crop_name, new Set());
      if (c.land_id) map.get(c.crop_name).add(String(c.land_id));
    });
    return Array.from(map.entries())
      .map(([name, landSet]) => ({ name, fieldCount: landSet.size }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [crops]);

  // Fields where the selected crop is sown
  const fieldOptions = useMemo(() => {
    if (!cropName) return [];
    const map = new Map();
    (crops || []).forEach(c => {
      if (c.crop_name !== cropName || !c.land_id) return;
      if (!map.has(String(c.land_id))) {
        map.set(String(c.land_id), { land_id: String(c.land_id), field_name: c.field_name || `Field #${c.land_id}` });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.field_name.localeCompare(b.field_name));
  }, [crops, cropName]);

  const handleCropChange = (val) => {
    setCropName(val);
    setLandId('all');
    setReport(null);
    setError('');
  };

  const handleGenerate = async () => {
    if (!cropName) return alert('Please select a crop');
    if (!fromDate || !toDate) return alert('Please select both dates');
    if (fromDate > toDate) return alert('From date cannot be after To date');

    setLoading(true);
    setError('');
    setReport(null);
    try {
      const res = await axios.get(`${API}/agriculture/reports/crop-ledger`, {
        headers: headers(),
        params: { type, crop_name: cropName, land_id: landId, from: fromDate, to: toDate }
      });
      setReport(res.data.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load report');
    }
    setLoading(false);
  };

  // Build grouped structure: per field → crop groups (one per sowing) + field-level block
  const grouped = useMemo(() => {
    if (!report) return [];
    const fields = new Map();

    const ensureField = (id, name) => {
      const key = String(id ?? 'none');
      if (!fields.has(key)) {
        fields.set(key, { land_id: id, field_name: name || 'Unassigned field', cropGroups: new Map(), fieldRows: [] });
      }
      return fields.get(key);
    };

    (report.crop_rows || []).forEach(r => {
      const f = ensureField(r.land_id, r.field_name);
      const ck = String(r.crop_id);
      if (!f.cropGroups.has(ck)) {
        f.cropGroups.set(ck, {
          crop_id: r.crop_id,
          crop_name: r.crop_name,
          crop_type: r.crop_type,
          sowing_date: r.sowing_date,
          area: formatArea(r.area_sown_kanals, r.area_sown_marlas),
          rows: []
        });
      }
      f.cropGroups.get(ck).rows.push(r);
    });

    (report.field_rows || []).forEach(r => {
      const f = ensureField(r.land_id, r.field_name);
      f.fieldRows.push(r);
    });

    return Array.from(fields.values()).map(f => {
      const cropGroups = Array.from(f.cropGroups.values()).map(g => ({
        ...g,
        subtotal: g.rows.reduce((s, r) => s + Number(r.amount || 0), 0)
      })).sort((a, b) => new Date(a.sowing_date || 0) - new Date(b.sowing_date || 0));

      const fieldLevelTotal = f.fieldRows.reduce((s, r) => s + Number(r.amount || 0), 0);
      const cropTotal = cropGroups.reduce((s, g) => s + g.subtotal, 0);

      return { ...f, cropGroups, fieldLevelTotal, fieldTotal: cropTotal + fieldLevelTotal };
    }).sort((a, b) => (a.field_name || '').localeCompare(b.field_name || ''));
  }, [report]);

  const grandTotal = useMemo(() => grouped.reduce((s, f) => s + f.fieldTotal, 0), [grouped]);
  const txnCount = useMemo(
    () => (report ? (report.crop_rows?.length || 0) + (report.field_rows?.length || 0) : 0),
    [report]
  );

  const selectedFieldLabel = landId === 'all'
    ? `All Acres (${fieldOptions.length})`
    : (fieldOptions.find(f => f.land_id === String(landId))?.field_name || '—');

  const handlePDF = () => {
    if (!report || grouped.length === 0) return alert('Generate a report first');

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setTextColor(44, 62, 80);
    doc.text('Sohaib Enterprises - Agriculture', pageW / 2, 15, { align: 'center' });
    doc.setFontSize(13);
    doc.text(title, pageW / 2, 22, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(85, 85, 85);
    doc.text(`Crop: ${cropName}`, 14, 32);
    doc.text(`Acre / Field: ${selectedFieldLabel}`, 14, 38);
    doc.text(`Period: ${fmtDate(fromDate)} to ${fmtDate(toDate)}`, 14, 44);
    doc.text(`Printed: ${new Date().toLocaleString('en-PK')}`, pageW - 14, 32, { align: 'right' });

    let startY = 52;

    grouped.forEach(f => {
      f.cropGroups.forEach(g => {
        const head = [[
          `${f.field_name} - ${g.crop_name}${g.crop_type ? ' (' + g.crop_type + ')' : ''}${g.area ? ' - ' + g.area : ''} - sown ${fmtDate(g.sowing_date)}`,
          '', '', '', ''
        ]];
        autoTable(doc, {
          startY,
          head,
          body: [],
          theme: 'plain',
          headStyles: { fillColor: [232, 245, 233], textColor: [23, 52, 4], fontSize: 9, fontStyle: 'bold' },
          margin: { left: 14, right: 14 }
        });
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY,
          head: [['Date', 'Category', 'Description', 'Paid By', 'Amount']],
          body: g.rows.map(r => [
            fmtDate(r.transaction_date),
            r.category || '-',
            r.description || '-',
            r.reference_no || '-',
            fmtMoney(r.amount)
          ]),
          foot: [['', '', '', 'Subtotal', `Rs. ${fmtMoney(g.subtotal)}`]],
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [248, 249, 250], textColor: [85, 85, 85], fontSize: 8 },
          footStyles: { fillColor: [245, 245, 245], textColor: [44, 62, 80], fontStyle: 'bold', fontSize: 8 },
          columnStyles: { 0: { cellWidth: 22 }, 3: { cellWidth: 24 }, 4: { cellWidth: 26, halign: 'right' } },
          margin: { left: 14, right: 14 }
        });
        startY = doc.lastAutoTable.finalY + 4;
      });

      if (f.fieldRows.length > 0) {
        autoTable(doc, {
          startY,
          head: [[`Field-level ${type} - ${f.field_name} (not crop-specific)`, '', '', '', '']],
          body: [],
          theme: 'plain',
          headStyles: { fillColor: [240, 240, 240], textColor: [85, 85, 85], fontSize: 9, fontStyle: 'bold' },
          margin: { left: 14, right: 14 }
        });
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY,
          head: [['Date', 'Category', 'Description', 'Paid By', 'Amount']],
          body: f.fieldRows.map(r => [
            fmtDate(r.transaction_date),
            r.category || '-',
            r.description || '-',
            r.reference_no || '-',
            fmtMoney(r.amount)
          ]),
          foot: [['', '', '', 'Subtotal', `Rs. ${fmtMoney(f.fieldLevelTotal)}`]],
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [248, 249, 250], textColor: [85, 85, 85], fontSize: 8 },
          footStyles: { fillColor: [245, 245, 245], textColor: [44, 62, 80], fontStyle: 'bold', fontSize: 8 },
          columnStyles: { 0: { cellWidth: 22 }, 3: { cellWidth: 24 }, 4: { cellWidth: 26, halign: 'right' } },
          margin: { left: 14, right: 14 }
        });
        startY = doc.lastAutoTable.finalY + 4;
      }

      autoTable(doc, {
        startY,
        body: [[`Total - ${f.field_name}`, `Rs. ${fmtMoney(f.fieldTotal)}`]],
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2.5, fontStyle: 'bold', fillColor: [234, 243, 222], textColor: [23, 52, 4] },
        columnStyles: { 1: { halign: 'right', cellWidth: 40 } },
        margin: { left: 14, right: 14 }
      });
      startY = doc.lastAutoTable.finalY + 6;
    });

    autoTable(doc, {
      startY,
      body: [[`GRAND TOTAL (${isExpense ? 'Expense' : 'Income'})`, `Rs. ${fmtMoney(grandTotal)}`]],
      theme: 'grid',
      styles: { fontSize: 11, cellPadding: 3.5, fontStyle: 'bold', fillColor: [59, 109, 17], textColor: [255, 255, 255] },
      columnStyles: { 1: { halign: 'right', cellWidth: 50 } },
      margin: { left: 14, right: 14 }
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, pageW / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
    }

    const safeCrop = cropName.replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`${type}_report_${safeCrop}_${fromDate}_to_${toDate}.pdf`);
  };

  const th = { padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#555', borderBottom: '1px solid #eee' };
  const td = { padding: '10px 12px', fontSize: '13px', color: '#444', borderBottom: '1px solid #f2f2f2' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1100, padding: '24px', overflowY: 'auto' }}>
      <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '1000px', padding: '28px', marginBottom: '40px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>{title}</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => window.print()} disabled={!report} style={{ padding: '9px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: report ? 'pointer' : 'not-allowed', fontWeight: '600', fontSize: '13px', opacity: report ? 1 : 0.5 }}>🖨️ Print</button>
            <button onClick={handlePDF} disabled={!report} style={{ padding: '9px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: report ? 'pointer' : 'not-allowed', fontWeight: '600', fontSize: '13px', opacity: report ? 1 : 0.5 }}>⬇️ Download PDF</button>
            <button onClick={onClose} style={{ padding: '9px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>✕</button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '18px', marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
            <div>
              <label style={labelStyle}>Crop *</label>
              <select style={inputStyle} value={cropName} onChange={e => handleCropChange(e.target.value)}>
                <option value="">-- Select Crop --</option>
                {cropNameOptions.map(c => (
                  <option key={c.name} value={c.name}>{c.name} ({c.fieldCount} field{c.fieldCount === 1 ? '' : 's'})</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Acre / Field</label>
              <select style={inputStyle} value={landId} onChange={e => { setLandId(e.target.value); setReport(null); }} disabled={!cropName}>
                <option value="all">{cropName ? `All Acres (${fieldOptions.length})` : '-- Select Crop first --'}</option>
                {fieldOptions.map(f => <option key={f.land_id} value={f.land_id}>{f.field_name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>From</label>
              <input style={inputStyle} type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setReport(null); }} />
            </div>
            <div>
              <label style={labelStyle}>To</label>
              <input style={inputStyle} type="date" value={toDate} onChange={e => { setToDate(e.target.value); setReport(null); }} />
            </div>
          </div>
          <div style={{ marginTop: '14px' }}>
            <button onClick={handleGenerate} disabled={loading || !cropName} style={{ padding: '11px 26px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: (loading || !cropName) ? 'not-allowed' : 'pointer', fontWeight: '600', opacity: (loading || !cropName) ? 0.6 : 1 }}>
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '14px 18px', background: '#fdecea', borderRadius: '10px', borderLeft: '4px solid #e74c3c', color: '#a32d2d', fontSize: '13px', marginBottom: '18px' }}>{error}</div>
        )}

        {!report && !loading && !error && (
          <div style={{ textAlign: 'center', padding: '50px', color: '#7f8c8d', fontSize: '14px' }}>
            Select a crop, acre and date range, then click Generate Report.
          </div>
        )}

        {report && grouped.length === 0 && (
          <div style={{ textAlign: 'center', padding: '50px', color: '#7f8c8d', fontSize: '14px' }}>
            No {type} records found for <strong>{cropName}</strong> in this period.
          </div>
        )}

        {report && grouped.length > 0 && (
          <>
            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '20px' }}>
              <div style={{ background: 'white', border: '1px solid #eee', borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Total {isExpense ? 'Expense' : 'Income'}</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: accent }}>Rs. {fmtMoney(grandTotal)}</div>
              </div>
              <div style={{ background: 'white', border: '1px solid #eee', borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Transactions</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50' }}>{txnCount}</div>
              </div>
              <div style={{ background: 'white', border: '1px solid #eee', borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Fields Covered</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50' }}>{grouped.length}</div>
              </div>
            </div>

            {grouped.map(f => (
              <div key={String(f.land_id)} style={{ marginBottom: '18px' }}>

                {f.cropGroups.map(g => (
                  <div key={g.crop_id} style={{ border: '1px solid #eee', borderRadius: '12px', overflow: 'hidden', marginBottom: '10px' }}>
                    <div style={{ background: '#eaf3de', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#173404' }}>
                        {f.field_name} — {g.crop_name}{g.crop_type ? ` (${g.crop_type})` : ''}{g.area ? ` · ${g.area}` : ''} · sown {fmtDate(g.sowing_date)}
                      </span>
                      <span style={{ fontSize: '12px', color: '#3b6d11' }}>{g.rows.length} {g.rows.length === 1 ? 'entry' : 'entries'}</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#fafafa' }}>
                          <th style={{ ...th, width: '110px' }}>Date</th>
                          <th style={{ ...th, width: '130px' }}>Category</th>
                          <th style={th}>Description</th>
                          <th style={{ ...th, width: '110px' }}>Paid By</th>
                          <th style={{ ...th, width: '120px', textAlign: 'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map(r => (
                          <tr key={r.id}>
                            <td style={td}>{fmtDate(r.transaction_date)}</td>
                            <td style={td}>{r.category || '—'}</td>
                            <td style={td}>{r.description || '—'}</td>
                            <td style={td}>{r.reference_no || '—'}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: '600' }}>{fmtMoney(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f7f7f7' }}>
                          <td colSpan={4} style={{ padding: '11px 12px', fontSize: '13px', fontWeight: '700', color: '#2c3e50' }}>Subtotal — {g.crop_name} (sown {fmtDate(g.sowing_date)})</td>
                          <td style={{ padding: '11px 12px', fontSize: '13px', fontWeight: '700', color: '#2c3e50', textAlign: 'right' }}>Rs. {fmtMoney(g.subtotal)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ))}

                {f.fieldRows.length > 0 && (
                  <div style={{ border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', marginBottom: '10px' }}>
                    <div style={{ background: '#f0f0f0', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#555' }}>
                        Field-level {isExpense ? 'expenses' : 'income'} — {f.field_name} (not crop-specific)
                      </span>
                      <span style={{ fontSize: '12px', color: '#777' }}>{f.fieldRows.length} {f.fieldRows.length === 1 ? 'entry' : 'entries'}</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {f.fieldRows.map(r => (
                          <tr key={r.id}>
                            <td style={{ ...td, width: '110px' }}>{fmtDate(r.transaction_date)}</td>
                            <td style={{ ...td, width: '130px' }}>{r.category || '—'}</td>
                            <td style={td}>{r.description || '—'}</td>
                            <td style={{ ...td, width: '110px' }}>{r.reference_no || '—'}</td>
                            <td style={{ ...td, width: '120px', textAlign: 'right', fontWeight: '600' }}>{fmtMoney(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f7f7f7' }}>
                          <td colSpan={4} style={{ padding: '11px 12px', fontSize: '13px', fontWeight: '700', color: '#555' }}>Subtotal — field-level</td>
                          <td style={{ padding: '11px 12px', fontSize: '13px', fontWeight: '700', color: '#555', textAlign: 'right' }}>Rs. {fmtMoney(f.fieldLevelTotal)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                <div style={{ background: '#eaf3de', borderRadius: '10px', padding: '11px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#173404' }}>Total — {f.field_name}</span>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: '#173404' }}>Rs. {fmtMoney(f.fieldTotal)}</span>
                </div>
              </div>
            ))}

            <div style={{ background: '#3b6d11', borderRadius: '12px', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: '#eaf3de' }}>Grand Total — {selectedFieldLabel}</span>
              <span style={{ fontSize: '20px', fontWeight: '700', color: '#eaf3de' }}>Rs. {fmtMoney(grandTotal)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CropFinanceReportModal;